import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Plus, Minus, ShoppingCart, Package, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddFnBModal({ open, onClose, products, onAddItems, onFavoriteToggle }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState({}); // { productId: qty }
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Reset cart when modal closes
  useEffect(() => {
    if (!open) {
      setCart({});
      setSearchTerm('');
      setSelectedCategory('all');
    }
  }, [open]);

  // Get unique categories - case-insensitive grouping (max 5 including all and favorite)
  const hasFavorites = products.some(p => p.is_favorite);
  
  // Create category map with case-insensitive keys
  const categoryMap = new Map();
  products.forEach(p => {
    if (p.category) {
      const normalized = p.category.toLowerCase();
      if (!categoryMap.has(normalized)) {
        // Store the first occurrence with proper capitalization
        categoryMap.set(normalized, p.category.charAt(0).toUpperCase() + p.category.slice(1).toLowerCase());
      }
    }
  });
  
  const uniqueProductCategories = Array.from(categoryMap.values());
  const categories = hasFavorites 
    ? ['all', 'favorite', ...uniqueProductCategories.slice(0, 3)]
    : ['all', ...uniqueProductCategories.slice(0, 4)];

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = selectedCategory === 'all' 
      || (selectedCategory === 'favorite' && product.is_favorite)
      || (product.category && product.category.toLowerCase() === selectedCategory.toLowerCase());
    return matchSearch && matchCategory;
  }).sort((a, b) => {
    // Sort favorites first
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;
    return 0;
  });

  // Calculate cart totals
  const cartItems = Object.entries(cart).map(([productId, qty]) => {
    const product = products.find(p => p.id === productId);
    return { ...product, qty };
  });
  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const cartCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);

  const handleIncrement = (productId) => {
    const product = products.find(p => p.id === productId);
    const currentQty = cart[productId] || 0;
    
    // Check stock for non-composite products
    if (product.type !== 'composite' && currentQty >= product.stock) {
      toast.error('Stok tidak mencukupi!');
      return;
    }
    
    setCart(prev => ({
      ...prev,
      [productId]: currentQty + 1
    }));
  };

  const handleDecrement = (productId) => {
    const currentQty = cart[productId] || 0;
    if (currentQty > 0) {
      if (currentQty === 1) {
        const newCart = { ...cart };
        delete newCart[productId];
        setCart(newCart);
      } else {
        setCart(prev => ({
          ...prev,
          [productId]: currentQty - 1
        }));
      }
    }
  };

  const handleToggleFavorite = async (productId, e) => {
    e.stopPropagation(); // Prevent card click
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/api/products/${productId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Notify parent to refresh products
        if (onFavoriteToggle) {
          onFavoriteToggle();
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Gagal mengubah status favorit');
    }
  };

  const handleConfirm = () => {
    if (cartCount === 0) {
      toast.error('Pilih minimal 1 produk!');
      return;
    }

    // Convert cart to items array
    const items = cartItems.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      qty: item.qty,
      category: item.category
    }));

    onAddItems(items);
    onClose();
    toast.success(`${cartCount} item berhasil ditambahkan!`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-orange-500/50 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 p-6 text-white flex-shrink-0 border-b-2 border-orange-400/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold flex items-center gap-2">
                <ShoppingCart className="h-8 w-8" />
                 Tambah Pesanan F&B
              </h2>
              <p className="text-orange-100 text-sm mt-1">Pilih produk yang ingin ditambahkan</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-6 bg-slate-800/50 border-b border-slate-700 space-y-4 flex-shrink-0">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cari produk... (Chitato, Indomie, dll)"
              className="w-full pl-12 pr-4 py-3 bg-slate-700 border-2 border-slate-600 rounded-xl text-white placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            <style>{`
              .overflow-x-auto::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {category === 'all' ? ' Semua' : category === 'favorite' ? '⭐ Favorit' : ` ${category}`}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-6" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
          <style>{`
            .flex-1.overflow-y-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Produk tidak ditemukan</p>
              <p className="text-gray-500 text-sm mt-2">Coba kata kunci lain atau ubah kategori</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredProducts.map((product) => {
                const qty = cart[product.id] || 0;
                const isOutOfStock = product.type !== 'composite' && product.stock === 0;
                
                return (
                  <div
                    key={product.id}
                    className={`relative bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl border-2 p-4 transition-all ${
                      qty > 0 
                        ? 'border-orange-500 shadow-lg shadow-orange-500/30' 
                        : 'border-slate-600 hover:border-slate-500'
                    } ${isOutOfStock ? 'opacity-50' : ''}`}
                  >
                    {/* Badge */}
                    {qty > 0 && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow-lg">
                        {qty}
                      </div>
                    )}
                    
                    {/* Badge "HABIS" - top right */}
                    {isOutOfStock && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold z-10">
                        HABIS
                      </div>
                    )}

                    {/* Product Image */}
                    {product.image_url && (
                      <div className="mb-3">
                        <img
                          src={`${API}${product.image_url}`}
                          alt={product.name}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="mb-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-bold text-white text-lg flex-1">{product.name}</h3>
                        {/* Favorite Star - next to product name */}
                        <button
                          onClick={(e) => handleToggleFavorite(product.id, e)}
                          className="p-1 bg-slate-900/50 hover:bg-slate-900/80 rounded-full transition-all flex-shrink-0"
                        >
                          <Star 
                            className={`h-4 w-4 ${
                              product.is_favorite 
                                ? 'fill-yellow-400 text-yellow-400' 
                                : 'text-slate-400 hover:text-yellow-400'
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-orange-400 font-bold text-xl">{formatCurrency(product.price)}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {product.type === 'composite' ? ' Komposit' : ` Stok: ${product.stock}`}
                      </p>
                    </div>

                    {/* Quantity Controls */}
                    {!isOutOfStock && (
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => handleDecrement(product.id)}
                          disabled={qty === 0}
                          className="p-2 bg-slate-600 hover:bg-red-600 text-white rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        
                        <span className="text-white font-bold text-lg min-w-[2rem] text-center">
                          {qty}
                        </span>
                        
                        <button
                          onClick={() => handleIncrement(product.id)}
                          className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Cart Summary */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 border-t-2 border-orange-500/50 flex-shrink-0">
          {/* Cart Summary */}
          {cartCount > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-400/50 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-orange-200 text-sm font-semibold">Total Item</p>
                  <p className="text-white text-2xl font-black">{cartCount} Item</p>
                </div>
                <div className="text-right">
                  <p className="text-orange-200 text-sm font-semibold">Subtotal</p>
                  <p className="text-orange-300 text-3xl font-black">{formatCurrency(cartTotal)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-slate-600 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-bold"
            >
               Batal
            </button>
            <button
              onClick={handleConfirm}
              disabled={cartCount === 0}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all font-black shadow-lg shadow-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
               Tambah ke Pesanan ({cartCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
